/**
 * Sample rules presets for the presets page (`?fixtures=1`, dev and preview
 * only: the same gate as the places pages, `isFixtureMode`). Typed as the
 * query results, so they cannot drift from the selections.
 *
 * Six presets with varied roles (role words as the backend uses them). The
 * sample places are the place pages' own, each given a default for new tables
 * (The Parlor's: Open table); its rooms already run House rules and Open
 * table. The templates are the place pages' three (two tied to The Parlor,
 * naming House rules and Open table) plus two more: one naming the event
 * presets, one naming a slug no preset has. The one repeat is the place
 * pages' (Hearts at the Parlor, through its template). Tournament has no slug
 * (so no template can name it) and is used nowhere. Every name and number
 * here is made up; the page labels it "sample".
 */
import type { PlaceWithRoomsData, PresetData, PresetRoles } from "@/data/presets-api";
import type { EventTemplateData } from "@/data/place-schedule-api";
import { PLACE_FIXTURES, REPEAT_FIXTURES, TEMPLATE_FIXTURES } from "@/features/places/fixtures";
import { whereUsedOf, type WhereUsed } from "@/features/presets/presets";

/** A made-up icon, inline (a sample never reaches the network). */
const SAMPLE_ICON =
  "data:image/svg+xml," +
  encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><rect width="20" height="20" rx="5" fill="slateblue"/></svg>');

const HOST = "@Host";
const MOD = "@Mod";
const SEATED = "@InSeat";
const ON_CALL = "@OnCall";
const OFF_CALL = "@OffCall";
const ON_STAGE = "@OnStage";
const OFF_STAGE = "@OffStage";

/** Roles where only the host (and mods, `withMods`) may act, then `extra` on top. */
function roles(extra: Partial<PresetRoles>, { withMods = true }: { withMods?: boolean } = {}): PresetRoles {
  const staff = withMods ? [HOST, MOD] : [HOST];
  return {
    adjustGameSettings: staff,
    advanceEventToFinalResults: [HOST],
    changeGame: staff,
    createSubgathering: [],
    doGameAdmin: staff,
    endEvent: [HOST],
    endGame: staff,
    endGathering: [HOST],
    inviteToCall: staff,
    inviteToSeat: staff,
    inviteToStage: staff,
    kickMember: staff,
    modifyGatheringSettings: [HOST],
    muteUserGlobally: staff,
    removeFromSeatAndCall: staff,
    removeFromStage: staff,
    selfJoinCall: [SEATED],
    selfJoinSeat: [OFF_CALL],
    selfJoinStage: [],
    startGame: staff,
    ...extra,
  };
}

function preset(
  id: string,
  name: string,
  slug: string | null,
  sortScore: number,
  rest: Partial<Omit<PresetData, "id" | "name" | "slug" | "sortScore">>,
): PresetData {
  return {
    id,
    name,
    slug,
    description: "",
    iconUrl: "",
    gatheringVisibility: "PUBLIC",
    selectable: true,
    sortScore,
    state: { official: false, public: true },
    idleGatheringCleanupEnabled: true,
    idleGatheringGracePeriodSeconds: 600,
    idleUserCleanupEnabled: true,
    idleUserGracePeriodSeconds: 300,
    roles: roles({}),
    ...rest,
  };
}

/** The six sample presets, deliberately out of column order (the page sorts
 *  by sort score, then name). The ids and slugs of House rules and Open table
 *  are the ones the sample places' rooms carry. */
export const PRESET_FIXTURES: PresetData[] = [
  preset("sample-preset-tournament", "Tournament", null, 40, {
    description: "Seated players only; the host runs the games.",
    selectable: false,
    state: { official: true, public: true },
    idleUserCleanupEnabled: false,
    roles: roles({ selfJoinCall: [], selfJoinSeat: [], changeGame: [HOST] }, { withMods: false }),
  }),
  preset("sample-preset-house", "House rules", "house-rules", 20, {
    description: "The main room's rules.",
    state: { official: true, public: true },
    roles: roles({ createSubgathering: [HOST, MOD, SEATED], selfJoinStage: [OFF_STAGE] }),
  }),
  preset("sample-preset-social", "Social", "social", 10, {
    description: "Anyone may start the game.",
    roles: roles({
      startGame: [HOST, MOD, SEATED],
      changeGame: [HOST, MOD, SEATED],
      createSubgathering: [HOST, MOD, SEATED, ON_CALL],
      selfJoinCall: [SEATED, OFF_CALL],
    }),
  }),
  preset("sample-preset-open", "Open table", "open-table", 30, {
    iconUrl: SAMPLE_ICON,
    roles: roles({ startGame: [HOST, SEATED], selfJoinCall: [SEATED, OFF_CALL] }),
  }),
  preset("sample-preset-event-lobby", "Event lobby", "event-lobby", 50, {
    gatheringVisibility: "FRIEND_VISIBLE",
    selectable: false,
    state: { official: true, public: false },
    idleGatheringCleanupEnabled: false,
    idleGatheringGracePeriodSeconds: 0,
    roles: roles({ selfJoinSeat: [], selfJoinCall: [], inviteToStage: [HOST], removeFromStage: [HOST, ON_STAGE] }),
  }),
  preset("sample-preset-event-rooms", "Event multi-room", "event-multi-room", 50, {
    gatheringVisibility: "PRIVATE",
    selectable: false,
    state: { official: true, public: false },
    roles: roles({ advanceEventToFinalResults: [HOST, MOD], endEvent: [HOST, MOD] }),
  }),
];

/** Each sample place's default for new tables, by preset id (absent: none). */
const TABLE_DEFAULTS: Record<string, string> = {
  "sample-parlor": "sample-preset-open",
  "sample-ben-and-jerrys": "sample-preset-social",
};

/** The place pages' sample places, each with its default for new tables. */
export const PRESET_PLACE_FIXTURES: PlaceWithRoomsData[] = PLACE_FIXTURES.map((place) => {
  const preset = TABLE_DEFAULTS[place.id];
  return { ...place, defaultGatheringSettingsForSubGatherings: preset ? { id: preset } : null };
});

const MINUTE = 60 * 1000;

/** The place pages' sample templates, plus one naming the two event presets
 *  (by slug) and one naming a slug no preset has. */
export const PRESET_TEMPLATE_FIXTURES: EventTemplateData[] = [
  ...TEMPLATE_FIXTURES,
  {
    id: "sample-tpl-finals",
    name: "Finals night",
    defaultDuration: 120 * MINUTE,
    templateJson: JSON.stringify({
      title: "Finals night",
      placeId: "sample-parlor",
      gatheringActivitySlug: "activity_liars_dice",
      gatheringSettingsTagSlug: "event-multi-room",
      lobbyGatheringSettingsTagSlug: "event-lobby",
    }),
  },
  {
    id: "sample-tpl-cup",
    name: "Spring cup",
    defaultDuration: 45 * MINUTE,
    templateJson: JSON.stringify({
      title: "Spring cup",
      placeId: null,
      gatheringActivitySlug: "activity_trivia",
      gatheringSettingsTagSlug: "spring-cup-rules",
      lobbyGatheringSettingsTagSlug: "event-lobby",
    }),
  },
];

/** The where-used lists as the page builds them from the samples. */
export function sampleWhereUsed(): WhereUsed {
  return whereUsedOf(PRESET_FIXTURES, {
    places: PRESET_PLACE_FIXTURES,
    templates: PRESET_TEMPLATE_FIXTURES,
    repeats: REPEAT_FIXTURES,
  });
}
